import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Wand2, FileText, Loader2, Sparkles, CheckCircle2, GraduationCap,
  CalendarDays, ListChecks, BookMarked, BrainCircuit,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATS = [
  { key: "exams", label: "Exams & Assessments", Icon: GraduationCap, color: "text-indigo-600" },
  { key: "events", label: "Events & Holidays", Icon: CalendarDays, color: "text-amber-600" },
  { key: "tasks", label: "Tasks & Homework", Icon: ListChecks, color: "text-rose-600" },
  { key: "curriculum", label: "Curriculum Units", Icon: BookMarked, color: "text-purple-600" },
];

export default function TrainAI() {
  const [docs, setDocs] = useState([]);
  const [selDoc, setSelDoc] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [sel, setSel] = useState({});

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const toggleKb = async (d, enabled) => {
    setDocs((prev) => prev.map((x) => (x.id === d.id ? { ...x, in_kb: enabled } : x)));
    try {
      await api.put(`/documents/${d.id}/kb`, { enabled });
      toast.success(enabled ? `"${d.original_filename}" added to AI knowledge` : `Removed from AI knowledge`);
    } catch {
      toast.error("Could not update");
      load();
    }
  };

  const analyze = async () => {
    if (!selDoc) return;
    setAnalyzing(true);
    setProposal(null);
    try {
      const start = await api.post("/train/analyze", { doc_id: selDoc });
      const jobId = start.data.job_id;
      let data = null;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/train/analyze/${jobId}`);
        if (st.data.status === "done") { data = st.data.result; break; }
        if (st.data.status === "error") throw new Error(st.data.error || "failed");
      }
      if (!data) throw new Error("timeout");
      setProposal(data);
      const s = {};
      CATS.forEach((c) => { s[c.key] = (data[c.key] || []).map((_, i) => i); });
      setSel(s);
      const total = CATS.reduce((a, c) => a + (data[c.key] || []).length, 0);
      if (total === 0) toast("No structured data found in this document.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read this document.");
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleItem = (cat, i) => {
    setSel((prev) => {
      const cur = prev[cat] || [];
      return { ...prev, [cat]: cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i] };
    });
  };

  const apply = async () => {
    if (!proposal) return;
    const payload = {};
    CATS.forEach((c) => { payload[c.key] = (proposal[c.key] || []).filter((_, i) => (sel[c.key] || []).includes(i)); });
    const total = Object.values(payload).reduce((a, arr) => a + arr.length, 0);
    if (total === 0) { toast("Select at least one item to apply."); return; }
    setApplying(true);
    try {
      const r = await api.post("/train/apply", payload);
      const a = r.data.applied;
      toast.success(`Site updated — ${a.exams} exams, ${a.events} events, ${a.tasks} tasks, ${a.curriculum} curriculum.`);
      setProposal(null);
    } catch {
      toast.error("Could not update the site. Try again.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 to-indigo-600 p-7 text-white">
        <div className="absolute -right-10 -top-10 w-52 h-52 bg-white/10 rounded-full blur-2xl" />
        <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-3"><BrainCircuit className="w-6 h-6" /></div>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold">Train AI & Update the Site</h2>
        <p className="text-indigo-100 mt-1 max-w-xl">Feed uploaded documents to the AI tutor, and let it pull exams, events, tasks and curriculum out of them — you approve what gets added.</p>
      </motion.div>

      {/* Knowledge base */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="kb-section">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-purple-500" /> AI Knowledge Base</h3>
        <p className="text-xs text-slate-500 mb-4">Turn a document ON to let the AI Tutor use it automatically when answering (no need to pick it each time).</p>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {docs.length === 0 && <p className="text-sm text-slate-400">No documents yet. Upload some in the Documents tab.</p>}
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3">
              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 truncate">{d.original_filename}</p>
                <p className="text-xs text-slate-400">{d.subject}</p>
              </div>
              <Switch data-testid={`kb-toggle-${d.id}`} checked={!!d.in_kb} onCheckedChange={(v) => toggleKb(d, v)} />
            </div>
          ))}
        </div>
      </div>

      {/* Auto-update */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="update-section">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1"><Wand2 className="w-5 h-5 text-indigo-500" /> Update the Site from a Document</h3>
        <p className="text-xs text-slate-500 mb-4">Pick a document, let AI scan it, then approve the items to add to Exams, Events, Tasks and Curriculum.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={selDoc} onValueChange={(v) => { setSelDoc(v); setProposal(null); }}>
            <SelectTrigger data-testid="train-doc-select" className="rounded-xl sm:w-96"><SelectValue placeholder="Choose a document…" /></SelectTrigger>
            <SelectContent>
              {docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.original_filename}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="analyze-btn" onClick={analyze} disabled={!selDoc || analyzing} className="rounded-xl bg-indigo-600 hover:bg-indigo-700">
            {analyzing ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Scanning…</> : <><Wand2 className="w-4 h-4 mr-1" /> Analyze document</>}
          </Button>
        </div>

        {proposal && (
          <div className="mt-5 space-y-5" data-testid="proposal">
            {CATS.map((c) => {
              const items = proposal[c.key] || [];
              if (items.length === 0) return null;
              const Icon = c.Icon;
              return (
                <div key={c.key}>
                  <p className={`text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5 ${c.color}`}><Icon className="w-3.5 h-3.5" /> {c.label} ({items.length})</p>
                  <div className="space-y-1.5">
                    {items.map((it, i) => (
                      <label key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3 cursor-pointer hover:bg-slate-50">
                        <Checkbox data-testid={`item-${c.key}-${i}`} checked={(sel[c.key] || []).includes(i)} onCheckedChange={() => toggleItem(c.key, i)} className="mt-0.5" />
                        <div className="min-w-0 flex-1 text-sm">
                          {c.key === "exams" && <span><b>{it.subject}</b> — {it.topic || "Assessment"} <span className="text-slate-400">· {it.date}</span></span>}
                          {c.key === "events" && <span><b>{it.title}</b> <span className="text-slate-400">· {it.date}{it.type === "holiday" ? " · Holiday" : ""}</span></span>}
                          {c.key === "tasks" && <span><b>{it.title}</b> <span className="text-slate-400">· {it.subject}{it.due_date ? " · due " + it.due_date : ""}</span></span>}
                          {c.key === "curriculum" && <span><b>{it.subject}</b> <span className="text-slate-400">· {(it.units || []).length} units</span></span>}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            {CATS.every((c) => (proposal[c.key] || []).length === 0) && (
              <p className="text-sm text-slate-400">No exams, events, tasks or curriculum found in this document.</p>
            )}
            <Button data-testid="apply-btn" onClick={apply} disabled={applying} className="rounded-xl bg-emerald-600 hover:bg-emerald-700">
              {applying ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Applying…</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Apply selected to site</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
